import { type Filter } from '@cubejs-client/core'
import { type BucketOverflowSide } from './numericBucketing'

// One dimension's value is the label itself; two or more are a JSON tuple, so a null stays distinct from any string.
export const encodeSegment = (values: readonly (string | null)[]): string | null => {
  if (values.length === 0) return null
  if (values.length === 1) return values[0] ?? null
  return JSON.stringify(values)
}

// Inverse of encodeSegment. The ≥2 path tolerates a bare single-dim label (left over while a
// freshly-added breakdown's query loads): JSON.parse throwing here would crash the whole page mid-render.
export const decodeSegment = (rawLabel: string | null, dimensionCount: number): (string | null)[] => {
  if (dimensionCount <= 1) return [rawLabel]
  if (rawLabel == null) return []
  try {
    const parsed: unknown = JSON.parse(rawLabel)
    return Array.isArray(parsed) ? (parsed as (string | null)[]) : [rawLabel]
  } catch {
    return [rawLabel]
  }
}

// A Cube filter selecting exactly the given segment combinations, for push-down:
//   • one breakdown   → `member IN [values]` (plus `notSet` when Unknown is among them)
//   • two+ breakdowns → OR of per-segment ANDs:  (a∧x) ∨ (b∧y) …
// A null dimension value maps to `notSet` (the Unknown bucket). Returns undefined when there's nothing to filter.
export const buildSegmentFilter = (segments: Iterable<string | null>, breakdowns: string[], view: string): Filter | undefined => {
  if (breakdowns.length === 0) return undefined
  const segs = [...segments]
  if (segs.length === 0) return undefined
  const members = breakdowns.map((b) => `${view}.${b}`)

  if (breakdowns.length === 1) {
    const member = members[0] as string
    const values = segs.filter((s): s is string => s != null)
    const parts: Filter[] = []
    if (values.length > 0) parts.push({ member, operator: 'equals', values })
    if (segs.some((s) => s == null)) parts.push({ member, operator: 'notSet' })
    return parts.length === 1 ? parts[0] : { or: parts }
  }

  const perSegment = (rawLabel: string | null): Filter => {
    const values = decodeSegment(rawLabel, breakdowns.length)
    const conditions: Filter[] = members.map((member, i) =>
      values[i] != null ? { member, operator: 'equals', values: [values[i] as string] } : { member, operator: 'notSet' }
    )
    return { and: conditions }
  }
  const ors = segs.map(perSegment)
  return ors.length === 1 ? ors[0] : { or: ors }
}

// Two underscores each side, like the pie's `__others__`, and a shape no FLOOR() result can take. The
// sentinels live in segment keys only: they are never persisted and never sent to Cube.
export const SENTINELS: Record<BucketOverflowSide, string> = { under: '__bucket_under__', over: '__bucket_over__' }

export const bucketSentinelSide = (value: string | null): BucketOverflowSide | null =>
  value === SENTINELS.under ? 'under' : value === SENTINELS.over ? 'over' : null

// A Cube cell as the chart names its bar (buildBreakdownLabels): a string, or null for Unknown. Cube sends strings,
// numbers and booleans; anything else is no value.
const segmentValue = (value: unknown): string | null =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : null

// A Cube row's bar: one raw value per breakdown dimension, in the dimensions' order rather than the row's.
export const rowSegment = (row: Record<string, unknown>, view: string, dimensions: readonly string[]): (string | null)[] =>
  dimensions.map((dimension) => segmentValue(row[`${view}.${dimension}`]))

// An overflow row has no band dimension: its bar is the other dimensions' values with the side's sentinel at the
// band's index, the key the in-range bars share (the frontend's stampBucketSentinel places it there too).
export const overflowRowSegment = (
  row: Record<string, unknown>,
  view: string,
  otherDimensions: readonly string[],
  bandIndex: number,
  side: BucketOverflowSide
): (string | null)[] => {
  const others = rowSegment(row, view, otherDimensions)
  return [...others.slice(0, bandIndex), SENTINELS[side], ...others.slice(bandIndex)]
}

/** Whether any dimension of the segment is an outside-range sentinel. A bare value never parses as an array, so no dimension count is needed. */
export const hasBucketSentinel = (rawLabel: string | null): boolean =>
  rawLabel != null && decodeSegment(rawLabel, 2).some((value) => bucketSentinelSide(value) != null)

export const withoutBucketSentinels = (segments: Iterable<string | null>): (string | null)[] =>
  [...segments].filter((segment) => !hasBucketSentinel(segment))
